import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import { ArrivalsAgentService } from './arrivals-agent.service';
import { AlertService, AuthenticationService } from '@app/_services';

/**
 * Live board for the Arrivals Agent. Shows today's arrivals or departures
 * with room status, lifecycle dots, and preview/send controls.
 *
 * Date navigation: prev/next arrows, calendar date picker, and quick-link
 * buttons for Arrivals/Departures on Yesterday/Today/Tomorrow.
 */
@Component({
	selector: 'app-arrivals-agent-board',
	templateUrl: './arrivals-agent-board.component.html',
	styleUrls: ['./arrivals-agent-board.component.scss'],
	standalone: false
})
export class ArrivalsAgentBoardComponent implements OnInit {

	rows: any[] = [];
	filteredRows: any[] = [];
	loading = false;

	/** Active location filter — null means "All" */
	locationFilter: number | null = null;

	/** Location name map keyed by locationId */
	static readonly LOCATION_NAMES: { [id: number]: string } = {
		1:  'Maui',
		2:  'Park City',
		3:  'Big Island',
		4:  'Oahu',
		5:  'San Diego',
		6:  'Aspen',
		9:  'Bass Lake',
		10: 'Yosemite',
	};

	/** All filter options including "All" */
	readonly locationFilterOptions: Array<{ id: number | null; label: string }> = [
		{ id: null, label: 'All' },
		{ id: 1,    label: 'Maui' },
		{ id: 3,    label: 'Big Island' },
		{ id: 4,    label: 'Oahu' },
		{ id: 2,    label: 'Park City' },
		{ id: 5,    label: 'San Diego' },
		{ id: 6,    label: 'Aspen' },
		{ id: 9,    label: 'Bass Lake' },
		{ id: 10,   label: 'Yosemite' },
	];

	/** Per-unit refresh spinner state. Key = shortTermRentalId. */
	refreshing: { [strId: number]: boolean } = {};

	// Date navigation
	selectedDate: string;        // YYYY-MM-DD
	viewType: 'arrivals' | 'departures' = 'arrivals';

	// Preview/send modal state
	previewOpen = false;
	previewLoading = false;
	previewError: string = null;
	previewBody: string = null;
	previewVariables: any = null;
	previewDoorCodeValid = false;
	previewAlreadySent = false;
	selectedReservation: any = null;
	selectedStageCode: number = ArrivalsAgentService.STAGE_READY_FOR_CHECKIN;

	stages = ArrivalsAgentService.STAGES;
	roomStatuses = ArrivalsAgentService.ROOM_STATUSES;

	constructor(
		private arrivalsService: ArrivalsAgentService,
		private alertService: AlertService,
		private auth: AuthenticationService,
		private _activatedRoute: ActivatedRoute,
	) {
		// Default to today (YYYY-MM-DD)
		this.selectedDate = this.toDateStr(new Date());
	}

	ngOnInit(): void {
		// Optional query-param handoff so other dashboards can link here with a
		// specific date / view preselected (e.g. the operations dashboard's
		// Arrivals tile opens "/arrivals-agent?date=YYYY-MM-DD&type=arrivals").
		// Bad / missing params silently fall back to today / arrivals.
		const params  = this._activatedRoute.snapshot.queryParamMap;
		const dateParam = params.get('date');
		const typeParam = params.get('type');
		if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
			this.selectedDate = dateParam;
		}
		if (typeParam === 'arrivals' || typeParam === 'departures') {
			this.viewType = typeParam;
		}
		this.loadBoard();
	}

	// -----------------------------------------------------------------------
	// Date navigation
	// -----------------------------------------------------------------------

	/** ISO YYYY-MM-DD for a Date object using local date parts. */
	private toDateStr(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	dateFromStr(s: string): Date {
		const [y, m, d] = s.split('-').map(Number);
		return new Date(y, m - 1, d);
	}

	get selectedDateDisplay(): string {
		const d = this.dateFromStr(this.selectedDate);
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
	}

	get isToday(): boolean {
		return this.selectedDate === this.toDateStr(new Date());
	}

	prevDay(): void {
		const d = this.dateFromStr(this.selectedDate);
		d.setDate(d.getDate() - 1);
		this.selectedDate = this.toDateStr(d);
		this.loadBoard();
	}

	nextDay(): void {
		const d = this.dateFromStr(this.selectedDate);
		d.setDate(d.getDate() + 1);
		this.selectedDate = this.toDateStr(d);
		this.loadBoard();
	}

	goToToday(): void {
		this.selectedDate = this.toDateStr(new Date());
		this.loadBoard();
	}

	/** Quick-link: set date offset from today and view type, then reload. */
	quickNav(viewType: 'arrivals' | 'departures', dayOffset: number): void {
		this.viewType = viewType;
		const d = new Date();
		d.setDate(d.getDate() + dayOffset);
		this.selectedDate = this.toDateStr(d);
		this.loadBoard();
	}

	/** Returns true if this quick-nav button is the current active selection. */
	isActiveNav(viewType: 'arrivals' | 'departures', dayOffset: number): boolean {
		const d = new Date();
		d.setDate(d.getDate() + dayOffset);
		return this.viewType === viewType && this.selectedDate === this.toDateStr(d);
	}

	onDateInputChange(value: string): void {
		if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
			this.selectedDate = value;
			this.loadBoard();
		}
	}

	// -----------------------------------------------------------------------
	// Data loading
	// -----------------------------------------------------------------------

	loadBoard(): void {
		this.loading = true;
		this.arrivalsService.getBoard(this.selectedDate, this.viewType)
			.pipe(first())
			.subscribe(
				rows => {
					this.rows = rows || [];
					this.applyLocationFilter();
					this.loading = false;
				},
				err => {
					console.error('arrivals board load failed', err);
					this.alertService.error(err?.error?.error || err?.message || 'Failed to load board');
					this.loading = false;
				}
			);
	}

	setLocationFilter(id: number | null): void {
		this.locationFilter = id;
		this.applyLocationFilter();
	}

	private applyLocationFilter(): void {
		if (this.locationFilter === null) {
			this.filteredRows = this.rows;
		} else {
			this.filteredRows = this.rows.filter(r => r.locationId === this.locationFilter);
		}
	}

	locationChip(locationId: number): { name: string; bg: string } {
		switch (locationId) {
			case 1:  return { name: 'Maui',       bg: '#0d7a5f' };
			case 2:  return { name: 'Park City',  bg: '#7c3aed' };
			case 3:  return { name: 'Big Island', bg: '#c05621' };
			case 4:  return { name: 'Oahu',       bg: '#2563eb' };
			case 5:  return { name: 'San Diego',  bg: '#b45309' };
			case 6:  return { name: 'Aspen',      bg: '#1d4ed8' };
			case 9:  return { name: 'Bass Lake',  bg: '#065f46' };
			case 10: return { name: 'Yosemite',   bg: '#9d174d' };
			default: return { name: locationId != null ? 'Loc ' + locationId : '—', bg: '#888' };
		}
	}

	// -----------------------------------------------------------------------
	// Room status helpers
	// -----------------------------------------------------------------------

	roomStatusChip(occupancyStatus: number): { name: string; color: string } {
		return this.roomStatuses[occupancyStatus] || { name: 'Unknown', color: '#999' };
	}

	// -----------------------------------------------------------------------
	// Reservation type chip helpers
	// -----------------------------------------------------------------------

	private static readonly RES_TYPE_CHIPS: { [key: number]: { name: string; color: string } } = {
		1:  { name: 'Owner',             color: 'hotpink' },
		2:  { name: 'Owner Guest',       color: 'lightpink' },
		15: { name: 'Owner Guest Paid',  color: 'lightpink' },
		14: { name: 'Hold',              color: '#f4e04d' },
		16: { name: 'Travel Agent',      color: '#c0e0ff' },
		3:  { name: 'Guest',             color: '#e8f4fd' },
		4:  { name: 'Maintenance',       color: '#ccc' },
		5:  { name: 'HK Block',          color: '#ccc' },
		6:  { name: 'Owner Arrival Prep', color: '#ccc' },
		7:  { name: 'Onboarding',        color: '#ccc' },
		8:  { name: 'Offboarding',       color: '#ccc' },
		9:  { name: 'Pictures',          color: '#ccc' },
		10: { name: 'Inventory',         color: '#ccc' },
		11: { name: 'Delivery',          color: '#ccc' },
		12: { name: 'Owner Block',       color: '#ccc' },
		13: { name: 'Office Block',      color: '#ccc' },
	};

	resTypeChip(type: number): { name: string; color: string } {
		return ArrivalsAgentBoardComponent.RES_TYPE_CHIPS[type]
			|| { name: type != null ? 'Type ' + type : '—', color: '#e8f4fd' };
	}

	// -----------------------------------------------------------------------
	// Reservation status chip helpers
	// -----------------------------------------------------------------------

	private static readonly RES_STATUS_CHIPS: { [key: string]: { name: string; color: string; textColor: string } } = {
		'QO': { name: 'Open',        color: 'lightgreen', textColor: '#222' },
		'QH': { name: 'Hold',        color: '#f4e04d',    textColor: '#222' },
		'QE': { name: 'Expired',     color: '#9c27b0',    textColor: '#fff' },
		'OP': { name: 'Reserved',    color: '#bbb',       textColor: '#222' },
		'OC': { name: 'Closed',      color: '#2e7d32',    textColor: '#fff' },
		'PP': { name: 'Postponed',   color: 'lightblue',  textColor: '#222' },
		'OX': { name: 'Canceled',    color: 'darkred',    textColor: '#fff' },
		'NA': { name: 'Maintenance', color: '#555',       textColor: '#fff' },
		'OO': { name: 'Owner Occ',   color: 'hotpink',    textColor: '#222' },
		'OG': { name: 'Owner Guest', color: 'lightpink',  textColor: '#222' },
	};

	resStatusChip(status: string): { name: string; color: string; textColor: string } {
		return ArrivalsAgentBoardComponent.RES_STATUS_CHIPS[status]
			|| { name: status || '—', color: '#eee', textColor: '#333' };
	}

	// -----------------------------------------------------------------------
	// Lifecycle dot helpers
	// -----------------------------------------------------------------------

	dotStatus(row: any, stageCode: number): 'sent' | 'failed' | 'queued' | 'empty' {
		const hist: any[] = row?.stageHistory || [];
		const match = hist.find((h: any) => h?.msgStage === stageCode);
		if (!match) return 'empty';
		// OUTBOX (5) means queued with Twilio — treat as sent for dot display.
		// Twilio delivery receipts may update to SENT (2) later, but the message
		// was already submitted successfully.
		if (match.status === ArrivalsAgentService.MSG_STATUS_SENT
			|| match.status === ArrivalsAgentService.MSG_STATUS_OUTBOX) return 'sent';
		if (match.status === ArrivalsAgentService.MSG_STATUS_FAILED) return 'failed';
		return 'queued';
	}

	dotTitle(row: any, stage: any): string {
		const hist: any[] = row?.stageHistory || [];
		const match = hist.find((h: any) => h?.msgStage === stage.code);
		if (!match) return `${stage.label} — not yet sent`;
		const when = match.sentDate ? new Date(match.sentDate).toLocaleString() : '';
		const statusName = match.status === 2 ? 'Sent'
			: match.status === 4 ? 'Failed'
			: match.status === 1 ? 'Draft'
			: match.status === 5 ? 'Queued'
			: 'Status ' + match.status;
		return `${stage.label} — ${statusName}${when ? ' ' + when : ''}`;
	}

	// -----------------------------------------------------------------------
	// Preview / Send
	// -----------------------------------------------------------------------

	openPreview(row: any, stageCode: number = this.selectedStageCode): void {
		this.selectedReservation = row;
		this.selectedStageCode = stageCode;
		this.previewOpen = true;
		this.previewLoading = true;
		this.previewError = null;
		this.previewBody = null;
		this.arrivalsService.preview(row.reservationId, stageCode).pipe(first()).subscribe(
			r => {
				this.previewBody = r?.body || '(no template found for this unit/stage)';
				this.previewVariables = r?.variables || {};
				this.previewDoorCodeValid = !!r?.doorCodeValid;
				this.previewAlreadySent = !!r?.alreadySent;
				this.previewLoading = false;
			},
			err => {
				this.previewError = err?.error?.error || err?.message || 'Preview failed';
				this.previewLoading = false;
			}
		);
	}

	confirmSend(): void {
		if (!this.selectedReservation) return;
		const userName = this.auth?.currentUserValue?.username || 'override';
		this.arrivalsService.send(this.selectedReservation.reservationId, this.selectedStageCode, userName)
			.pipe(first()).subscribe(
				r => {
					if (r?.sent) {
						this.alertService.success('Arrival text sent (msgId ' + r.msgId + ')');
						this.loadBoard(); // refresh dots
					} else {
						this.alertService.error(r?.reason || 'Send skipped — check server log');
					}
					this.previewOpen = false;
				},
				err => this.alertService.error(err?.error?.error || err?.message || 'Send failed')
			);
	}

	closePreview(): void {
		this.previewOpen = false;
	}

	// -----------------------------------------------------------------------
	// Door code dialog
	// -----------------------------------------------------------------------

	doorDialogOpen = false;
	doorDialogLoading = false;
	doorDialogSaving = false;
	doorDialogError: string = null;
	doorDialogRow: any = null;
	doorAccessCodeId: number = null;
	doorCodeValue: string = '';

	openDoorDialog(row: any): void {
		this.doorDialogRow = row;
		this.doorCodeValue = row.accessCodeFrontDoor || '';
		this.doorAccessCodeId = null;
		this.doorDialogError = null;
		this.doorDialogOpen = true;
		this.doorDialogLoading = true;
		this.arrivalsService.getAccessCodes(row.reservationId).pipe(first()).subscribe(
			r => {
				const codes: any[] = r?.data?.accessCodes || r?.accessCodes || [];
				const frontDoor = codes.find((c: any) => c.lockLocation === 1) || codes[0];
				if (frontDoor) {
					this.doorAccessCodeId = frontDoor.accessCodeId;
					this.doorCodeValue = frontDoor.accessCode || '';
				}
				this.doorDialogLoading = false;
			},
			() => { this.doorDialogLoading = false; }
		);
	}

	saveDoorCode(): void {
		if (!this.doorDialogRow) return;
		this.doorDialogSaving = true;
		this.doorDialogError = null;
		const dto: any = {
			accessCode: this.doorCodeValue?.trim(),
			lockLocation: 1,
			doorName: 'Front Door',
			accessType: 1,
			reservationId: this.doorDialogRow.reservationId,
			shortTermRentalId: this.doorDialogRow.shortTermRentalId
		};
		const call$ = this.doorAccessCodeId
			? this.arrivalsService.updateAccessCode({ ...dto, accessCodeId: this.doorAccessCodeId })
			: this.arrivalsService.createAccessCode(dto);
		call$.pipe(first()).subscribe(
			() => {
				this.alertService.success('Door code saved');
				this.doorDialogOpen = false;
				this.doorDialogSaving = false;
				this.loadBoard();
			},
			err => {
				this.doorDialogError = err?.error?.error || err?.message || 'Save failed';
				this.doorDialogSaving = false;
			}
		);
	}

	closeDoorDialog(): void {
		this.doorDialogOpen = false;
	}

	refreshUnit(row: any): void {
		const strId: number = row?.shortTermRentalId;
		if (!strId) { return; }
		this.refreshing[strId] = true;
		this.arrivalsService.refresh(strId).pipe(first()).subscribe(
			r => {
				const sentCount    = r?.sent?.length    || 0;
				const skippedCount = r?.skipped?.length || 0;
				const status       = r?.currentStatus != null
					? (this.roomStatusChip(r.currentStatus).name)
					: 'unknown';
				if (sentCount > 0) {
					this.alertService.success(
						`${r.propertyCode || strId} — refreshed (status: ${status}). Sent ${sentCount} text(s).`
					);
				} else {
					this.alertService.warn(
						`${r.propertyCode || strId} — refreshed (status: ${status}). No new texts (${skippedCount} skipped).`
					);
				}
				this.loadBoard();
			},
			err => {
				this.alertService.error(err?.error?.error || err?.message || 'Refresh failed');
				this.refreshing[strId] = false;
			},
			() => { this.refreshing[strId] = false; }
		);
	}

	stageChanged(code: number): void {
		if (this.selectedReservation) {
			this.openPreview(this.selectedReservation, code);
		}
	}

	// -----------------------------------------------------------------------
	// Room Status override dialog
	// -----------------------------------------------------------------------

	readonly roomStatusOptions: Array<{ code: number; name: string; color: string }> = [
		{ code: 1, name: 'Occupied',             color: 'yellow'    },
		{ code: 2, name: 'Dirty',                color: '#c62828'   },
		{ code: 3, name: 'Housekeeping',         color: 'hotpink'   },
		{ code: 4, name: 'Ready For Inspection', color: '#aaa'      },
		{ code: 5, name: 'Inspection',           color: 'purple'    },
		{ code: 6, name: 'Clean Ready',          color: '#2e7d32'   },
		{ code: 7, name: 'Checked-In',           color: '#2e7d32'   },
		{ code: 8, name: 'In Progress',          color: '#0288d1'   },
	];

	roomStatusDialogOpen  = false;
	roomStatusDialogRow: any = null;
	roomStatusDialogValue: number | null = null;
	roomStatusDialogSaving = false;
	roomStatusDialogError: string = null;

	openRoomStatusDialog(row: any): void {
		this.roomStatusDialogRow   = row;
		this.roomStatusDialogValue = row.occupancyStatus ?? null;
		this.roomStatusDialogError = null;
		this.roomStatusDialogOpen  = true;
	}

	saveRoomStatus(): void {
		if (!this.roomStatusDialogRow || this.roomStatusDialogValue == null) return;
		this.roomStatusDialogSaving = true;
		this.roomStatusDialogError  = null;
		this.arrivalsService.updateRoomStatus(
			this.roomStatusDialogRow.shortTermRentalId,
			this.roomStatusDialogValue
		).pipe(first()).subscribe(
			r => {
				// Patch the row in-place so the board updates without a full reload
				const row = this.rows.find(x => x.shortTermRentalId === this.roomStatusDialogRow.shortTermRentalId);
				if (row) {
					row.occupancyStatus     = r.occupancyStatus;
					row.occupancyStatusName = r.occupancyStatusName;
				}
				this.applyLocationFilter();
				this.alertService.success(
					`Room status updated to ${r.occupancyStatusName} for ${this.roomStatusDialogRow.propertyCode}`
				);
				this.roomStatusDialogOpen   = false;
				this.roomStatusDialogSaving = false;
			},
			err => {
				this.roomStatusDialogError  = err?.error?.error || err?.message || 'Save failed';
				this.roomStatusDialogSaving = false;
			}
		);
	}

	closeRoomStatusDialog(): void {
		this.roomStatusDialogOpen = false;
	}
}
